import React, { useCallback, useEffect, useRef, useState } from "react";

import "./AuctionTimeInput.css";
import { Button } from "@mui/material";
import { min } from "date-fns";
import useOutsideClick from "../../../useOutsideClick";
/** Utility for startDate checks */
const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const isSameDay = (date1, date2) => {
  return (
    date1 &&
    date2 &&
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const AuctionTimeInput = ({
  heading,
  value,
  onChange,
  error,
  errorMessage,
  disabled = false,
  startDate,
  startHours = 0,
  startMinutes = 0,
  minHr = 0,
}) => {
  const [open, setOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState({});
  const clockList = useRef(null);
  const inputRef = useRef(null);

  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");

  const [hours, setHours] = useState([]);
  const [minutes, setMinutes] = useState([]);

  useOutsideClick(
    inputRef,
    useCallback(() => setOpen(false), []),
  );
  const minutesStep = 15;
  const hoursMinutesCheckFunction = React.useCallback(() => {
    const mins = [];
    const hrs = [];
    for (let i = startHours + minHr; i <= 24; i++) {
      if (i < startHours) continue;
      const hr = i < 10 ? `0${i}` : `${i}`;
      hrs.push(hr);
    }
    // Start i at the upcoming nearest multiple of 15
    let startMinute = Math.ceil(startMinutes / 15) * 15;
    for (let i = startMinute; i < 60; i += minutesStep) {
      const min = i < 10 ? `0${i}` : `${i}`;
      mins.push(min);
    }
    setHours(hrs);
    setMinutes(mins);
  }, [minHr, startHours, startMinutes]);

  const hoursMinutesOpenFunction = React.useCallback(() => {
    const mins = [];
    const hrs = [];
    for (let i = startMinutes; i < 50; i += minutesStep) {
      const min = i < 10 ? `0${i}` : `${i}`;
      mins.push(min);
    }
    for (let i = startHours + minHr; i <= 24; i++) {
      const hr = i < 10 ? `0${i}` : `${i}`;
      hrs.push(hr);
    }
    setMinutes(mins);
    setHours(hrs);
  }, [minHr, startHours, startMinutes]);

  useEffect(() => {
    if (value) {
      const [hr, min] = value.split(":");
      setHour(hr || "");
      setMinute(min || "");
    } else {
      setHour("");
      setMinute("");
    }
  }, [value]);

  useEffect(() => {
    if (isSameDay(startDate, getToday())) {
      hoursMinutesCheckFunction();
    } else {
      hoursMinutesOpenFunction();
    }
  }, [hoursMinutesCheckFunction, hoursMinutesOpenFunction, startDate]);

  /** Handle selection */
  const handleHourInput = (hr) => {
    setHour(hr);
    if (minute) {
      onChange(`${hr}:${minute}`);
      setOpen(false);
    }
    if (Number(hr) !== Number(startHours)) {
      let mins = [];
      if (hr == 24) {
        mins.push(`00`);
      } else {
        for (let i = 0; i < 60; i += minutesStep) {
          const min = i < 10 ? `0${i}` : `${i}`;
          mins.push(min);
        }
      }
      setMinutes(mins);
    }
  };

  const handleMinuteInput = (min) => {
    setMinute(min);
    if (min !== "00") {
      let hrs = [];
      for (let i = startHours + minHr; i <= 23; i++) {
        const hr = i < 10 ? `0${i}` : `${i}`;
        hrs.push(hr);
      }
      setHours(hrs);
    }
    if (hour) {
      onChange(`${hour}:${min}`);
      setOpen(false);
    } else {
      let hrs = [];
      for (let i = startHours + minHr; i <= 24; i++) {
        const hr = i < 10 ? `0${i}` : `${i}`;
        hrs.push(hr);
      }
      setHours(hrs);
    }
  };

  return (
    <div ref={inputRef} className="time-picker-wrapper">
      <div className={`display_flex_row_center contentTimeHeader`}>
        <div className="contentHeading">{heading} </div>
      </div>

      {/* Input Box */}
      <div
        className={`input_select_cont ${error ? "inputErrorContainer" : ""}`}
        style={{
          height: `40px`,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
        onClick={() => {
          if (!disabled) setOpen((p) => !p);
        }}
      >
        <div className="display_flex_row_center timeInputValue">
          <input
            type="text"
            maxLength={2}
            value={hour}
            disabled={disabled}
            placeholder="HH"
            className="time-input"
            onChange={(e) => {
              const val = e.target.value;
              if (/^\d{0,2}$/.test(val) && (val === "" || Number(val) <= 24)) {
                setHour(val);
                if (minute) onChange(`${val}:${minute}`);
              }
            }}
          />
          <div className="display_flex_row_center separator">:</div>
          <input
            type="text"
            maxLength={2}
            value={minute}
            disabled={disabled}
            placeholder="MM"
            className="time-input"
            onChange={(e) => {
              const val = e.target.value;
              const validOptions = ["00", "15", "30", "45"];
              if (
                val === "" ||
                validOptions.some((opt) => opt.startsWith(val))
              ) {
                setMinute(val);
                if (hour) onChange(`${hour}:${val}`);
              }
            }}
            onBlur={() => {
              const validOptions = ["00", "15", "30", "45"];
              if (!minute) return;
              if (validOptions.includes(minute)) return;

              const numVal = Number(minute);
              let nearest = validOptions.reduce((a, b) =>
                Math.abs(Number(a) - numVal) < Math.abs(Number(b) - numVal)
                  ? a
                  : b,
              );
              setMinute(nearest);
              if (hour) onChange(`${hour}:${nearest}`);
            }}
          />
        </div>
      </div>

      {/* Error */}
      {error && <div className="timeErrorMessage">{error}</div>}

      {/* Portal Popup */}
      {open && (
        <div className="pop-up-body" style={popupStyle} ref={clockList}>
          <div style={{ display: "flex" }}>
            <div className="hr-list">
              {hours.map((hr) => (
                <div
                  key={hr}
                  onClick={() => handleHourInput(hr)}
                  className={`list-item ${hr === hour ? "selected" : ""}`}
                >
                  {hr}
                </div>
              ))}
            </div>
            <div className="hr-list">
              {minutes.map((min) => (
                <div
                  key={min}
                  onClick={() => handleMinuteInput(min)}
                  className={`list-item ${min === minute ? "selected" : ""}`}
                >
                  {min}
                </div>
              ))}
            </div>
          </div>
          <div className="clock-footer">
            <Button variant="text" onClick={() => setOpen(false)}>
              OK
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctionTimeInput;
